import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { 
  CheckCircle, 
  AlertCircle, 
  Video, 
  Upload, 
  FileText,
  UserCheck,
  Phone,
  Calendar,
  AlertTriangle
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

const WaiverFormPage = () => {
  const { bookingId: paramBookingId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const bookingId = paramBookingId || searchParams.get('bookingId');

  const [step, setStep] = useState('verify'); // verify, form, success
  const [bookingDetails, setBookingDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [inputBookingId, setInputBookingId] = useState(bookingId || '');
  
  // Terms and conditions
  const [medicalRequirements, setMedicalRequirements] = useState(null);
  const [terms, setTerms] = useState(null);
  
  // Jumpers data
  const [jumpers, setJumpers] = useState([]);

  useEffect(() => {
    if (bookingId) {
      fetchBookingDetails(bookingId);
    }
    fetchTerms();
  }, [bookingId]);

  const fetchBookingDetails = async (id) => {
    try {
      setLoading(true);
      setError('');
      const response = await axios.get(`${API}/api/waiver/booking/${id}`);
      setBookingDetails(response.data);
      
      // Initialize jumpers array
      const initialJumpers = Array(response.data.numberOfJumpers).fill(null).map((_, index) => ({
        name: '',
        mobileNumber: '',
        age: '',
        emergencyContactNumber: '',
        emergencyContactRelation: '',
        videoConsentUrl: '',
        videoFile: null,
        hasMedicalConditions: false,
        medicalConditionsDetails: '',
        medicalRequirementsRead: false,
        termsAccepted: false
      }));
      setJumpers(initialJumpers);
      setStep('form');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch booking details');
    } finally {
      setLoading(false);
    }
  };

  const fetchTerms = async () => {
    try {
      const [medicalResp, termsResp] = await Promise.all([
        axios.get(`${API}/api/locations`), // We'll get medical from locations page
        axios.get(`${API}/api/waiver/terms/waiver`).catch(() => null)
      ]);
      
      // For now, use static medical requirements
      setMedicalRequirements({
        content: `**Medical Requirements**\n\nBefore participating in bungee jumping, please read carefully:\n\n• You must be in good physical health\n• Free from heart conditions, high blood pressure, or epilepsy\n• Not pregnant\n• No recent surgeries or injuries\n• No history of back or neck problems\n• Not under the influence of alcohol or drugs\n\nIf you have any concerns about your health, please consult a doctor before jumping.`
      });
      
      if (termsResp) {
        setTerms(termsResp.data);
      } else {
        setTerms({
          content: `**Waiver of Liability**\n\nBy signing this waiver, you acknowledge that:\n\n1. Bungee jumping is an extreme sport with inherent risks\n2. You participate at your own risk\n3. You release Indya Bungy from all liability\n4. You have read and understood all safety instructions\n5. You meet all health and age requirements`
        });
      }
    } catch (err) {
      console.error('Failed to fetch terms:', err);
    }
  };

  const handleVerify = () => {
    if (!inputBookingId.trim()) {
      setError('Please enter booking ID');
      return;
    }
    navigate(`/web-checkin/${inputBookingId}`);
  };

  const updateJumper = (index, field, value) => {
    const updated = [...jumpers];
    updated[index] = { ...updated[index], [field]: value };
    setJumpers(updated);
  };

  const handleVideoRecord = async (index, file) => {
    if (!file) return;
    
    // Update jumper with file
    const updated = [...jumpers];
    updated[index].videoFile = file;
    setJumpers(updated);
  };

  const validateForm = () => {
    for (let i = 0; i < jumpers.length; i++) {
      const jumper = jumpers[i];
      if (!jumper.name.trim()) return `Jumper ${i + 1}: Name is required`;
      if (!jumper.mobileNumber.trim()) return `Jumper ${i + 1}: Mobile number is required`;
      if (!jumper.age || jumper.age < 10) return `Jumper ${i + 1}: Age must be at least 10 years`;
      if (!jumper.emergencyContactNumber.trim()) return `Jumper ${i + 1}: Emergency contact is required`;
      if (!jumper.emergencyContactRelation.trim()) return `Jumper ${i + 1}: Emergency contact relation is required`;
      if (!jumper.videoFile) return `Jumper ${i + 1}: Video consent is required`;
      if (!jumper.medicalRequirementsRead) return `Jumper ${i + 1}: Must read medical requirements`;
      if (!jumper.termsAccepted) return `Jumper ${i + 1}: Must accept terms and conditions`;
    }
    return null;
  };

  const handleSubmit = async () => {
    const validationError = validateForm();
    if (validationError) {
      alert(validationError);
      return;
    }

    try {
      setLoading(true);
      
      // Upload videos first
      const uploadedJumpers = await Promise.all(jumpers.map(async (jumper) => {
        const formData = new FormData();
        formData.append('video', jumper.videoFile);
        
        const uploadResp = await axios.post(`${API}/api/waiver/upload-consent`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        return {
          name: jumper.name,
          mobileNumber: jumper.mobileNumber,
          age: parseInt(jumper.age),
          emergencyContactNumber: jumper.emergencyContactNumber,
          emergencyContactRelation: jumper.emergencyContactRelation,
          videoConsentUrl: uploadResp.data.videoUrl,
          hasMedicalConditions: jumper.hasMedicalConditions,
          medicalConditionsDetails: jumper.medicalConditionsDetails,
          medicalRequirementsRead: jumper.medicalRequirementsRead,
          termsAccepted: jumper.termsAccepted
        };
      }));

      // Submit waiver
      await axios.post(`${API}/api/waiver/submit`, {
        bookingId: bookingDetails.bookingId,
        jumpers: uploadedJumpers
      });

      setStep('success');
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to submit waiver form');
    } finally {
      setLoading(false);
    }
  };

  if (step === 'verify') {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center px-4 py-12">
        <div className="max-w-md w-full">
          <div className="text-center mb-8">
            <img src="/logo.png" alt="Indya Bungy" className="h-20 mx-auto mb-6" />
            <h1 className="text-3xl font-black text-white mb-2">Web Check-in</h1>
            <p className="text-gray-400">Complete your waiver form before jumping</p>
          </div>

          <div className="bg-gray-800 border border-gray-700 rounded-2xl p-8">
            <label className="block text-white font-medium mb-3">Booking ID</label>
            <input
              type="text"
              value={inputBookingId}
              onChange={(e) => setInputBookingId(e.target.value.toUpperCase())}
              className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-yellow-400"
              placeholder="Enter your booking ID"
            />
            
            {error && (
              <div className="mt-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
                {error}
              </div>
            )}

            <button
              onClick={handleVerify}
              disabled={loading}
              className="w-full mt-6 px-6 py-3 bg-yellow-400 hover:bg-yellow-500 text-black font-bold rounded-lg transition-colors disabled:opacity-50"
            >
              {loading ? 'Verifying...' : 'Continue'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (step === 'form' && bookingDetails) {
    return (
      <div className="min-h-screen bg-black py-12 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <img src="/logo.png" alt="Indya Bungy" className="h-16 mx-auto mb-4" />
            <h1 className="text-3xl font-black text-white mb-2">Waiver of Liability Form</h1>
            <p className="text-gray-400">Complete this form before your jump</p>
          </div>

          {/* Booking Info */}
          <div className="bg-gray-800 border border-gray-700 rounded-xl p-6 mb-6">
            <h2 className="text-xl font-bold text-white mb-4">Booking Information</h2>
            <div className="grid md:grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-400">Booking ID:</span>
                <span className="text-white ml-2 font-bold">{bookingDetails.bookingId}</span>
              </div>
              <div>
                <span className="text-gray-400">Customer:</span>
                <span className="text-white ml-2">{bookingDetails.customerName}</span>
              </div>
              <div>
                <span className="text-gray-400">Location:</span>
                <span className="text-white ml-2">{bookingDetails.locationName}</span>
              </div>
              <div>
                <span className="text-gray-400">Number of Jumpers:</span>
                <span className="text-white ml-2">{bookingDetails.numberOfJumpers}</span>
              </div>
            </div>
          </div>

          {/* Jumpers Forms */}
          {jumpers.map((jumper, index) => (
            <JumperForm
              key={index}
              index={index}
              jumper={jumper}
              updateJumper={updateJumper}
              handleVideoRecord={handleVideoRecord}
              medicalRequirements={medicalRequirements}
              terms={terms}
            />
          ))}

          {/* Submit Button */}
          <div className="mt-8">
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="w-full px-6 py-4 bg-yellow-400 hover:bg-yellow-500 text-black font-bold text-lg rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center"
            >
              {loading ? 'Submitting...' : 'Submit Waiver Form'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (step === 'success') {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center">
          <CheckCircle className="mx-auto text-green-400 mb-6" size={64} />
          <h1 className="text-3xl font-black text-white mb-4">Form Submitted Successfully!</h1>
          <p className="text-gray-400 mb-8">
            Your waiver form has been submitted. Please show this confirmation at the jump site.
          </p>
          <div className="bg-gray-800 border border-gray-700 rounded-xl p-6 mb-6">
            <p className="text-gray-400 mb-2">Booking ID</p>
            <p className="text-2xl font-bold text-yellow-400">{bookingDetails.bookingId}</p>
          </div>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-3 bg-yellow-400 hover:bg-yellow-500 text-black font-bold rounded-lg transition-colors"
          >
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  return null;
};

// Jumper Form Component
const JumperForm = ({ index, jumper, updateJumper, handleVideoRecord, medicalRequirements, terms }) => {
  const videoRef = useRef(null);
  const [showMedicalModal, setShowMedicalModal] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [recording, setRecording] = useState(false);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleVideoRecord(index, file);
    }
  };

  return (
    <div className="bg-gray-800 border border-gray-700 rounded-xl p-6 mb-6">
      <h3 className="text-xl font-bold text-white mb-4">Jumper {index + 1}</h3>
      
      <div className="space-y-4">
        {/* Name */}
        <div>
          <label className="block text-white font-medium mb-2">Full Name</label>
          <input
            type="text"
            value={jumper.name}
            onChange={(e) => updateJumper(index, 'name', e.target.value)}
            className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400"
            placeholder="Enter full name"
          />
        </div>

        {/* Mobile & Age */}
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-white font-medium mb-2">Mobile Number</label>
            <input
              type="tel"
              value={jumper.mobileNumber}
              onChange={(e) => updateJumper(index, 'mobileNumber', e.target.value)}
              className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400"
              placeholder="10-digit number"
            />
          </div>
          <div>
            <label className="block text-white font-medium mb-2">Age</label>
            <input
              type="number"
              value={jumper.age}
              onChange={(e) => updateJumper(index, 'age', e.target.value)}
              className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400"
              placeholder="Age"
              min="10"
            />
          </div>
        </div>

        {/* Emergency Contact */}
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-white font-medium mb-2">Emergency Contact Number</label>
            <input
              type="tel"
              value={jumper.emergencyContactNumber}
              onChange={(e) => updateJumper(index, 'emergencyContactNumber', e.target.value)}
              className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400"
              placeholder="Emergency contact"
            />
          </div>
          <div>
            <label className="block text-white font-medium mb-2">Relation</label>
            <input
              type="text"
              value={jumper.emergencyContactRelation}
              onChange={(e) => updateJumper(index, 'emergencyContactRelation', e.target.value)}
              className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400"
              placeholder="e.g., Parent, Spouse"
            />
          </div>
        </div>

        {/* Medical Requirements */}
        <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center">
              <FileText className="text-blue-400 mr-2" size={20} />
              <span className="text-white font-medium">Medical Requirements</span>
            </div>
            <button
              type="button"
              onClick={() => setShowMedicalModal(true)}
              className="text-blue-400 text-sm hover:text-blue-300"
            >
              Read
            </button>
          </div>
          <label className="flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={jumper.medicalRequirementsRead}
              onChange={(e) => updateJumper(index, 'medicalRequirementsRead', e.target.checked)}
              className="mr-2"
            />
            <span className="text-gray-300 text-sm">I have read the medical requirements</span>
          </label>
          
          <label className="flex items-center cursor-pointer mt-2">
            <input
              type="checkbox"
              checked={jumper.hasMedicalConditions}
              onChange={(e) => updateJumper(index, 'hasMedicalConditions', e.target.checked)}
              className="mr-2"
            />
            <span className="text-gray-300 text-sm">I have medical conditions</span>
          </label>
          
          {jumper.hasMedicalConditions && (
            <textarea
              value={jumper.medicalConditionsDetails}
              onChange={(e) => updateJumper(index, 'medicalConditionsDetails', e.target.value)}
              className="w-full mt-2 px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400"
              placeholder="Please describe your medical conditions"
              rows={3}
            />
          )}
        </div>

        {/* Video Consent */}
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
          <div className="flex items-center mb-3">
            <Video className="text-yellow-400 mr-2" size={20} />
            <span className="text-white font-medium">Video Consent</span>
          </div>
          <p className="text-gray-300 text-sm mb-3">
            Record or upload a video stating: "I [Your Name] consent to participate in bungee jumping activity"
          </p>
          <input
            type="file"
            accept="video/*"
            capture="user"
            onChange={handleFileSelect}
            className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-yellow-400 file:text-black hover:file:bg-yellow-500"
          />
          {jumper.videoFile && (
            <p className="text-green-400 text-sm mt-2">✓ Video selected: {jumper.videoFile.name}</p>
          )}
        </div>

        {/* Terms & Conditions */}
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center">
              <AlertTriangle className="text-red-400 mr-2" size={20} />
              <span className="text-white font-medium">Terms & Conditions</span>
            </div>
            <button
              type="button"
              onClick={() => setShowTermsModal(true)}
              className="text-red-400 text-sm hover:text-red-300"
            >
              Read
            </button>
          </div>
          <label className="flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={jumper.termsAccepted}
              onChange={(e) => updateJumper(index, 'termsAccepted', e.target.checked)}
              className="mr-2"
            />
            <span className="text-gray-300 text-sm">I accept the terms and conditions</span>
          </label>
        </div>
      </div>

      {/* Medical Modal */}
      {showMedicalModal && (
        <Modal
          title="Medical Requirements"
          content={medicalRequirements?.content}
          onClose={() => setShowMedicalModal(false)}
        />
      )}

      {/* Terms Modal */}
      {showTermsModal && (
        <Modal
          title="Terms & Conditions"
          content={terms?.content}
          onClose={() => setShowTermsModal(false)}
        />
      )}
    </div>
  );
};

// Modal Component
const Modal = ({ title, content, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto">
        <div className="p-6">
          <h2 className="text-2xl font-black text-white mb-4">{title}</h2>
          <div className="text-gray-300 whitespace-pre-wrap mb-6">
            {content}
          </div>
          <button
            onClick={onClose}
            className="w-full px-6 py-3 bg-yellow-400 hover:bg-yellow-500 text-black font-bold rounded-lg transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default WaiverFormPage;
